// Apollo
import { useQuery } from "@apollo/client";

// Design System
import { Button, DatePicker, Modal, SelectDropdown } from "@toddle-edu/ds-web";
import { DownloadOutlined } from "@toddle-edu/ds-icons";

// External
import _ from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Constants
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

// Components
import SkeletonLoader from "UIComponents/SkeletonLoader";

// Utils
import { formatDate } from "Utils";

// Relative - Queries & Helpers
import { getSchoolTemplatesQuery } from "../../modules/queries";
import { isQueryLoading, getTemplateOptions } from "../../modules/helpers";

// Relative - Hooks
import { useDownloadTranscripts } from "../../hooks";

// CSS
import classes from "./SingleDownload.scss";

const SingleDownload = ({
  onClose,
  schoolId,
  selectedTemplate: propsSelectedTemplate,
  studentId,
  studentName,
  resetSelection,
  testId,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState(
    propsSelectedTemplate
  );
  const [issueDate, setIssueDate] = useState(moment());
  const { t } = useTranslation();
  const { data, networkStatus } = useQuery(getSchoolTemplatesQuery, {
    variables: { id: schoolId },
    fetchPolicy: "cache-and-network",
    skip: !schoolId,
  });

  const downloadTranscript = useDownloadTranscripts();

  const isLoading = isQueryLoading(data, networkStatus);

  const options = useMemo(() => {
    const templates = _.get(data, "node.transcriptTemplates.edges", []);
    return getTemplateOptions({
      templates,
      t,
    });
  }, [data, t]);

  useEffect(() => {
    setSelectedTemplate(prev => {
      const prevId = _.get(prev, "value");
      if (prevId) {
        const isPrevIdPresent = _.some(options, { value: prevId });
        if (isPrevIdPresent) return prev;
      }

      return _.first(options);
    });
  }, [options]);

  const handleTemplateSelect = useCallback(option => {
    setSelectedTemplate(option);
  }, []);

  const handleDownload = useCallback(() => {
    downloadTranscript({
      studentIds: [studentId],
      studentIdToSchoolIdMap: { [studentId]: schoolId },
      issueDate: issueDate,
      templateId: selectedTemplate?.value,
      label: `${selectedTemplate?.label}_${studentName}_${formatDate({
        date: issueDate,
        format: DATE_TIME_CONSTANTS.DD_MMM_YYYY,
      })}`,
    });
    onClose();
    if (resetSelection) resetSelection();
  }, [
    downloadTranscript,
    issueDate,
    onClose,
    resetSelection,
    schoolId,
    selectedTemplate,
    studentId,
    studentName,
  ]);

  return (
    <Modal isOpen onClose={onClose} width="552px" height="max-content">
      <Modal.Header
        testId={`${testId}-singleDownload`}
        title={t("common:download_with_label", {
          label: t("transcript:transcript"),
        })}
      />

      <Modal.Body>
        <div className={classes.body}>
          <div className={classes.field}>
            <div className="text-label">
              {t("transcript:transcript_template")}
            </div>

            {isLoading ? (
              <SkeletonLoader height="36px" width="100%" />
            ) : (
              <SelectDropdown
                options={options}
                value={selectedTemplate}
                onChange={handleTemplateSelect}
                placeholder={t("common:select_with_label", {
                  label: t("common:template"),
                })}
                isClearable={false}
                testId={`${testId}-singleDownload-templateDropdown`}
                maxMenuHeight={188}
              />
            )}
          </div>
          <div className={classes.field}>
            <div className="text-label">{t("common:issue_date")}</div>
            <DatePicker
              placeholder={t("common:issue_date")}
              format="DD MMM YYYY"
              value={_.isEmpty(issueDate) ? undefined : issueDate}
              onChange={setIssueDate}
              testId={`${testId}-singleDownload-issueDatePicker`}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className={classes.footer}>
          <Button
            type="outlined"
            variant="neutral"
            onClick={onClose}
            testId={`${testId}-singleDownload-cancel`}
          >
            {t("common:cancel")}
          </Button>

          <Button
            variant="progressive"
            icon={<DownloadOutlined />}
            testId={`${testId}-singleDownload-download`}
            disabled={isLoading || !selectedTemplate}
            onClick={handleDownload}
          >
            {t("common:download")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

SingleDownload.propTypes = {
  onClose: PropTypes.func.isRequired,
  schoolId: PropTypes.string.isRequired,
  selectedTemplate: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  studentId: PropTypes.string.isRequired,
  studentName: PropTypes.string.isRequired,
  resetSelection: PropTypes.func,
  testId: PropTypes.string,
};

SingleDownload.defaultProps = {
  selectedTemplate: null,
  resetSelection: null,
  testId: "",
};

export default memo(SingleDownload);
